import React, {
  useEffect,
  useMemo,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  bytesFormat,
  formatCurrency,
  formatPricePerUnit,
} from '@utils/formatters';
import {
  Col,
  Row,
  Skeleton,
  Descriptions,
  Divider,
  Tooltip,
  Button,
} from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { changeDocumentTitle } from '@utils/changeDocumentTitle';
import useSwr from '@hooks/useSwr';
import ConsumptionCard from '@components/ConsumptionCard';
import RoleChecker from '@components/RoleChecker';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import AddCredit from '@components/AddCredit';
import BackToListing from '@components/BackToListing';
import SessionContext from '@providers/Session/SessionContext';
import Requests from '@pages/Financial/Requests';
import CustomCard from '@components/CustomCard';
import CustomTag from '@components/CustomTag';
import AlertsEvents from '@pages/Alerts/Events';

const DetailedContract = () => {
  const { t } = useTranslation([
    'contracts',
    'requests',
    'credits',
    'attributes',
  ]);
  const { t: tMainContainer } = useTranslation('main');
  const { contractId } = useParams();
  const { pathname } = useLocation();

  const [showAddCredit, setShowAddCredit] = useState(false);
  const [showContracted, setShowContracted] = useState(false);

  const {
    effectiveRoles,
    customer: { customerLoggedName, customerLogged },
  } = useContext(SessionContext);

  const hasRole = useCallback(
    (role: RolesTypes) => effectiveRoles?.portal?.includes(role),
    [effectiveRoles],
  );

  const { data: contract } = useSwr(
    `/service-proxy/broker/virtual-plan/${contractId}?showContracted=${showContracted}`,
  );

  const contractDetails = useMemo(() => contract?.virtualContract, [contract]);

  useEffect(() => {
    if (customerLogged && contractDetails) {
      setShowContracted(customerLogged.id === contractDetails.customer?.id);
    }
  }, [contractDetails, customerLogged]);

  const contractView = useMemo(
    () => (contractDetails?.status === 'ARCHIVED' ? 'filed' : 'active'),
    [contractDetails],
  );

  // You can only add credit to a shared agreement
  const itsASharedContract = useMemo(
    () => contractDetails?.sharingType === 2,
    [contractDetails],
  );

  const customerDetails = useMemo(
    () => contractDetails?.customer,
    [contractDetails],
  );

  const consumption = useMemo(() => {
    const rateConsumption = contract?.saldoContratoAgrupador;

    return {
      rateConsumption: {
        updatedDate: rateConsumption?.dtAtualizacaoConsumo,
        totalFranchise: rateConsumption?.nuFranquiaTotal,
        franchiseConsumption: rateConsumption?.nuConsumoFranquia,
        surplusConsumption: rateConsumption?.nuConsumoExcedente,
        totalConsumption: rateConsumption?.nuConsumoTotal,
      },
      currentConsumption: {
        consumption: contract?.virtualContract?.currentConsumption,
        franchise: contract?.virtualContract?.currentFranchise,
      },
    };
  }, [contract]);

  useEffect(() => {
    changeDocumentTitle({
      t: tMainContainer,
      pathname,
      customerLoggedName,
      detailName: contractDetails?.planDesc,
    });
  }, [pathname, contractDetails, customerLoggedName, tMainContainer]);

  const contractTypeFormatted = useMemo(() => {
    if (contractDetails?.payPerUseFlag === 'S') {
      return t('attributes:type.payPerUse');
    }

    switch (contractDetails?.sharingType) {
      case 1:
        return t('attributes:type.sharedWithout');
      case 2:
        return t('attributes:type.sharedTotal');
      case 3:
        return t('attributes:type.sharedMixed');
      default:
        return '';
    }
  }, [contractDetails, t]);

  const handleActivatedTag = useCallback(
    (value: boolean) => (
      <CustomTag color={value ? 'green' : 'red'}>
        {t(
          `attributes:automaticBlocking.${value ? 'activated' : 'disabled'}`,
        ).toUpperCase()}
      </CustomTag>
    ),
    [t],
  );

  const typeOfPenalty = useMemo(
    () =>
      t(
        `attributes:typesOfPenalty.${
          contractDetails?.loyaltyType ?? 'noPenalty'
        }`,
      ),
    [t, contractDetails],
  );

  const renderExcPrice = useMemo(
    () => formatPricePerUnit(contractDetails?.megabyteExcPrice),
    [contractDetails?.megabyteExcPrice],
  );

  const contractItemsDescriptions = useMemo(
    () => [
      {
        label: t('attributes:type.title'),
        value: contractTypeFormatted,
        showItem: contractTypeFormatted,
      },
      {
        label: t('attributes:franchise'),
        value: bytesFormat(contractDetails?.franchise),
        showItem: contractDetails?.franchise,
      },
      {
        label: t('attributes:activationPrice'),
        value: formatCurrency(parseFloat(contractDetails?.activationPrice)),
        showItem: contractDetails?.activationPrice,
      },
      {
        label: t('attributes:automaticBlocking.title'),
        value: handleActivatedTag(contractDetails?.automaticBlocking),
        showItem:
          !showContracted ||
          (showContracted && contractDetails?.sharingType === 2),
      },
      {
        label: t('attributes:price'),
        value: formatCurrency(parseFloat(contractDetails?.price)),
        showItem:
          contractDetails?.price &&
          contractDetails?.operatorDiffChargeFlag === 'N',
      },
      {
        label: t('attributes:sharedFranchise'),
        value: bytesFormat(contractDetails?.sharedFranchise),
        showItem:
          contractDetails?.sharingType && contractDetails?.sharingType === 3,
      },
      {
        label: t('attributes:megabyteExcPrice'),
        value: renderExcPrice,
        showItem:
          !showContracted ||
          (showContracted && contractDetails?.sharingType === 2),
      },
      {
        label: t('attributes:typesOfPenalty.title'),
        value: typeOfPenalty,
        showItem: true,
      },
      {
        label: t('attributes:penaltyAmount'),
        value: formatCurrency(parseFloat(contractDetails?.penalty)),
        showItem:
          contractDetails?.penalty && contractDetails?.loyaltyType === 1,
      },
      {
        label: t('attributes:loyaltyTime'),
        value: contractDetails?.loyaltyTime,
        showItem: contractDetails?.loyaltyType === 2,
      },
      {
        label: t('attributes:planType'),
        value: t(
          `attributes:planTypes.${contractDetails?.planType?.toLowerCase()}`,
        ),
        showItem: contractDetails?.planType,
      },
      {
        label: t('attributes:planExpirationMonths'),
        value: `${contractDetails?.planExpirationMonths} ${t(
          'attributes:months',
        )}`,
        showItem: contractDetails?.planExpirationMonths,
      },
      {
        label: t('attributes:automaticRenewal'),
        value: contractDetails?.automaticRenewal
          ? t('attributes:hasTag.yes')
          : t('attributes:hasTag.no'),
        showItem: contractDetails?.planType !== 'STANDARD',
      },
      {
        label: t('attributes:iscaDurationInDays'),
        value: contractDetails?.iscaDurationInDays,
        showItem: contractDetails?.planType === 'BAIT',
      },
      {
        label: t('attributes:iscaMaxRenewal'),
        value: contractDetails?.iscaMaxRenewal,
        showItem: contractDetails?.planType === 'BAIT',
      },
    ],
    [
      t,
      contractTypeFormatted,
      contractDetails?.franchise,
      contractDetails?.activationPrice,
      contractDetails?.automaticBlocking,
      contractDetails?.sharingType,
      contractDetails?.price,
      contractDetails?.operatorDiffChargeFlag,
      contractDetails?.sharedFranchise,
      contractDetails?.penalty,
      contractDetails?.loyaltyType,
      contractDetails?.loyaltyTime,
      contractDetails?.planType,
      contractDetails?.planExpirationMonths,
      contractDetails?.automaticRenewal,
      contractDetails?.iscaDurationInDays,
      contractDetails?.iscaMaxRenewal,
      handleActivatedTag,
      showContracted,
      renderExcPrice,
      typeOfPenalty,
    ],
  );

  const clientItemsDescriptions = useMemo(
    () => [
      {
        label: t('attributes:name'),
        value: customerDetails?.name,
      },
      {
        label: t('attributes:nickname'),
        value: customerDetails?.nickname,
      },
      {
        label: t('attributes:tradingName'),
        value: customerDetails?.tradingName,
      },
      {
        label: t('attributes:cpfCnpj'),
        value: customerDetails?.cpfCnpj,
      },
      {
        label: t('attributes:customerType.title'),
        value: t(`attributes:customerType.${customerDetails?.type}`),
      },
    ],
    [t, customerDetails],
  );

  const DividerModel = useCallback(
    ({ title }: any) => (
      <Divider
        style={{
          marginTop: 24,
          marginBottom: 32,
          fontSize: '0.85rem',
          opacity: 0.7,
          textTransform: 'uppercase',
        }}
        orientation="left"
      >
        {title}
      </Divider>
    ),
    [],
  );

  return (
    <Skeleton loading={!contract} active>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col lg={24} sm={24} xs={24}>
          <CustomCard
            title={
              <BackToListing
                title={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {t('details.title')}
                    {itsASharedContract && (
                      <RoleChecker role={rolesConstants.ADD_CREDIT_TO_CONTRACT}>
                        <Tooltip title={t('credits:add-credit')}>
                          <Button
                            icon={
                              <DollarOutlined
                                style={{ fontSize: '1.6rem', marginLeft: 8 }}
                              />
                            }
                            type="link"
                            onClick={() => setShowAddCredit(true)}
                          />
                        </Tooltip>
                      </RoleChecker>
                    )}
                  </div>
                }
              />
            }
          >
            <Descriptions
              column={{ lg: 3, md: 2, sm: 2, xs: 1 }}
              style={{ marginTop: 24, padding: 24 }}
            >
              <Descriptions.Item
                // @ts-ignore
                key={t('attributes:planDesc')}
                label={t('attributes:planDesc')}
              >
                {contractDetails?.planDesc}
              </Descriptions.Item>
              {contractDetails?.motOperators && (
                <Descriptions.Item
                  // @ts-ignore
                  key={t('attributes:vendor')}
                  label={t('attributes:vendor')}
                >
                  {contractDetails?.motOperators?.name}
                </Descriptions.Item>
              )}

              {contractDetails?.externalId && (
                <Descriptions.Item
                  // @ts-ignore
                  key={t('attributes:externalId')}
                  label={t('attributes:externalId')}
                >
                  {contractDetails?.externalId}
                </Descriptions.Item>
              )}
            </Descriptions>
            <DividerModel title={t('details.billingModel')} />
            <Descriptions
              column={{ lg: 3, md: 2, sm: 2, xs: 1 }}
              style={{ padding: 24 }}
            >
              {contractItemsDescriptions?.map(
                ({ label, value, showItem }) =>
                  showItem &&
                  value && (
                    <Descriptions.Item key={label} label={label}>
                      {value}
                    </Descriptions.Item>
                  ),
              )}
            </Descriptions>

            <DividerModel title={t('attributes:customer')} />

            <Descriptions
              column={{ lg: 3, md: 2, sm: 2, xs: 1 }}
              style={{ padding: 24 }}
            >
              {clientItemsDescriptions?.map(
                ({ label, value }) =>
                  value && (
                    <Descriptions.Item key={label} label={label}>
                      {value}
                    </Descriptions.Item>
                  ),
              )}
            </Descriptions>
          </CustomCard>
        </Col>
      </Row>

      {contractDetails &&
        !(
          contractDetails?.sharingType === 1 &&
          contractDetails?.payPerUseFlag === 'N'
        ) && (
          <Row gutter={[16, 16]}>
            <Col lg={24} sm={24} xs={24}>
              <ConsumptionCard
                data={consumption?.rateConsumption}
                currentConsumption={consumption?.currentConsumption}
              />
            </Col>
          </Row>
        )}

      {hasRole(rolesConstants.VIEW_ALERTS) && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col lg={24} sm={24} xs={24}>
            <AlertsEvents
              defaultFilters={{
                showContracted,
                contractId,
              }}
              tableOnly
              isDetailsContracts
            />
          </Col>
        </Row>
      )}

      {contractView === 'active' && (
        <div style={{ marginTop: 16 }}>
          <Requests
            isInTheContractDetails
            defaultFilters={{
              showContracted,
              contractId: contractDetails?.id,
            }}
            defaultContractId={contractDetails?.id}
            defaultCustomerId={customerDetails?.id}
            defaultColumns={[
              'id',
              'name',
              'operatorDiffChargeFlag',
              'value',
              'activationPrice',
              'megabyteExcPrice',
              'automaticBlocking',
              'actions',
            ]}
          />
        </div>
      )}

      {itsASharedContract && showAddCredit && (
        <RoleChecker role={rolesConstants.ADD_CREDIT_TO_CONTRACT}>
          <AddCredit
            creditType="contract"
            idToAdd={contractDetails?.id}
            visible={showAddCredit}
            onClose={() => setShowAddCredit(false)}
            isPurchase={!!showContracted}
          />
        </RoleChecker>
      )}
    </Skeleton>
  );
};

export default DetailedContract;
